import { supabase } from "./client";
import type { CommandRow } from "../types/commands";
import type {
  MissionRow,
  MissionSpecRow,
  PayloadCategoryRow,
  PayloadCompositionRow,
  PayloadOptionRow,
} from "../types/payload";
import type { MissionRiskRow, MissionMitigationRow } from "../types/risk";
import type { MyGroundStation, MyGroupTle, UplinkStatus, WorkshopStatus, OpenWorkshop } from "../types/workshop";
import type {
  AdminWorkshopRow,
  AdminWorkshopMissionRow,
  AdminWorkshopGroupRow,
  AdminGroupUplinkRow,
  AdminGroundStationRow,
  AdminParticipantRow,
  AdminProfileRow,
  AdminMissionRow,
  AdminPayloadCategoryRow,
  AdminPayloadOptionRow,
} from "../types/admin";
import type { MissionPlatform, MissionOrbit } from "../pages/missions/platformOrbit";
import type { AdminGroupLaunchRow, AdminGroupTleRow, GroupLaunchChoiceRow, LaunchOptionRow, MyLaunch } from "../types/launch";

// Reads the current Supabase Auth session so the app can restore navigation and call authenticated edge functions.
export function getCurrentSession() {
  return supabase.auth.getSession();
}

// Reads the current Supabase Auth user for screens that need to map auth_user_id to an employee row.
export function getCurrentUser() {
  return supabase.auth.getUser();
}

// Subscribes to auth changes so navigation can refresh the visible role when login state changes.
export function onAuthStateChange(callback: () => void) {
  return supabase.auth.onAuthStateChange(() => {
    callback();
  });
}

// Signs an existing user in with email/password credentials from the entry screen.
export function signInWithPassword(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}

// Registers a new Supabase Auth account (a row in auth.users). The name goes
// along as user metadata: the "on_auth_user_created" trigger reads it when
// it creates the matching profiles row, so the client never inserts into
// profiles itself. Email confirmation is switched off in the project, so
// this returns a live session straight away.
export function signUpWithPassword(email: string, password: string, name?: { firstName?: string; lastName?: string }) {
  return supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        first_name: name?.firstName?.trim() || null,
        last_name: name?.lastName?.trim() || null,
      },
    },
  });
}

// The signed-in person's own profile row (name + role). RLS only lets a
// session read the profile linked to its own auth user, so this can't be
// used to read anyone else's.
export function fetchMyProfile(authUserId: string) {
  return supabase
    .from("profiles")
    .select("email,first_name,last_name,role")
    .eq("auth_user_id", authUserId)
    .maybeSingle<{ email: string; first_name: string | null; last_name: string | null; role: "admin" | "user" }>();
}

// The command of the caller's mission, for the command uplink screen (see
// supabase/sql/mission_commands.sql). Null until the caller has opened their
// group's uplink, or while the mission has no command yet -- other missions'
// commands are never returned.
export function fetchMyUplinkCommand() {
  return supabase.rpc("my_uplink_command").maybeSingle<CommandRow>();
}

/* ------------------------------------------------------------------ *
 * Missions, payloads and trade-off compositions (see
 * supabase/sql/007_missions_payloads.sql). Every mission page is driven
 * entirely by these -- adding a new mission is a SQL insert, not a new
 * .tsx file.
 * ------------------------------------------------------------------ */

const MISSION_COLUMNS =
  "id,slug,name,category,summary,objective,icon_key,platform_type,orbit_type,platform_detail,orbit_detail,image_url";

// The workshops a participant can choose from on the Dashboard: everything
// that isn't in the past (see supabase/sql/workshop_choice.sql), soonest first.
export async function fetchOpenWorkshops() {
  const res = await supabase.rpc("open_workshops");
  return { data: res.data as OpenWorkshop[] | null, error: res.error };
}

// Saves which workshop this profile takes part in. Switching workshops
// drops the mission and group chosen in the old one, server-side.
export function chooseWorkshop(workshopId: string) {
  return supabase.rpc("choose_workshop", { p_workshop_id: workshopId });
}

// Missions offered by a given workshop (via the workshop_missions join),
// for the Dashboard's mission-chooser buttons/tiles. workshop_missions also
// has an open SELECT policy.
export function fetchMissionsForWorkshop(workshopId: string) {
  return supabase
    .from("workshop_missions")
    .select(`mission_id, missions(${MISSION_COLUMNS})`)
    .eq("workshop_id", workshopId)
    .returns<{ mission_id: string; missions: MissionRow | null }[]>();
}

// Loads one mission for the mission view screen.
export function fetchMissionById(id: string) {
  return supabase.from("missions").select(MISSION_COLUMNS).eq("id", id).maybeSingle<MissionRow>();
}

/* ------------------------------------------------------------------ *
 * The mission sheet itself (specifications, trade-off rows, risks,
 * mitigations) belongs to a group, not to a mission: every group fills in
 * its own copy (see supabase/sql/groups.sql). Row-level security only
 * lets a group's members read or write its rows.
 * ------------------------------------------------------------------ */

const MISSION_SPEC_COLUMNS = "id,group_id,budget,main_aspect,weight_not_important,weight_kg,edited_by";

// Loads the budget/main-aspect/weight a group has chosen. Comes back null
// until someone in the group edits a field for the first time.
export function fetchMissionSpec(groupId: string) {
  return supabase
    .from("mission_specifications")
    .select(MISSION_SPEC_COLUMNS)
    .eq("group_id", groupId)
    .maybeSingle<MissionSpecRow>();
}

// Creates or updates the group's spec row in one call -- the first edit
// creates it, every edit after that just updates it.
export function upsertMissionSpec(groupId: string, patch: Partial<Omit<MissionSpecRow, "id" | "group_id">>) {
  return supabase
    .from("mission_specifications")
    .upsert({ group_id: groupId, ...patch }, { onConflict: "group_id" })
    .select(MISSION_SPEC_COLUMNS)
    .single<MissionSpecRow>();
}

/* ------------------------------------------------------------------ *
 * The payload catalogue (see supabase/sql/payload_catalogue.sql):
 * payload_categories are the payload TYPES ("Camera"), payload_options
 * the PAYLOADS themselves (a specific product of a type), and
 * mission_payload_options says which payloads each mission offers. All
 * three are readable by everyone; only admins can change them.
 * ------------------------------------------------------------------ */

const PAYLOAD_CATEGORY_COLUMNS = "id,label,custom_criterion_label,order_index";
const PAYLOAD_OPTION_COLUMNS =
  "id,category_id,name,manufacturer,summary,image_url,spec_sheet_url,rating_resolution,rating_energy,rating_mass,rating_cost,rating_trl,order_index";

// Every payload type, A-Z.
export function fetchPayloadCategories() {
  return supabase
    .from("payload_categories")
    .select(PAYLOAD_CATEGORY_COLUMNS)
    .order("label", { ascending: true })
    .returns<PayloadCategoryRow[]>();
}

// Every payload in the catalogue, A-Z.
export function fetchPayloadOptions() {
  return supabase
    .from("payload_options")
    .select(PAYLOAD_OPTION_COLUMNS)
    .order("name", { ascending: true })
    .returns<PayloadOptionRow[]>();
}

// Which payload is offered in which mission, for the whole catalogue.
export function fetchMissionPayloadAssignments() {
  return supabase
    .from("mission_payload_options")
    .select("mission_id,option_id")
    .returns<{ mission_id: string; option_id: string }[]>();
}

// The payloads one mission offers, and the types they belong to -- what the
// mission sheet builds its payload showcases and trade-off tables from.
export async function fetchMissionPayloads(
  missionId: string
): Promise<{ categories: PayloadCategoryRow[]; options: PayloadOptionRow[]; error: { message: string } | null }> {
  const none = { categories: [], options: [] };

  const assigned = await supabase
    .from("mission_payload_options")
    .select("option_id")
    .eq("mission_id", missionId)
    .returns<{ option_id: string }[]>();
  if (assigned.error) return { ...none, error: assigned.error };

  const optionIds = (assigned.data ?? []).map((row) => row.option_id);
  if (optionIds.length === 0) return { ...none, error: null };

  const options = await supabase
    .from("payload_options")
    .select(PAYLOAD_OPTION_COLUMNS)
    .in("id", optionIds)
    .order("name", { ascending: true })
    .returns<PayloadOptionRow[]>();
  if (options.error) return { ...none, error: options.error };

  const categoryIds = Array.from(new Set((options.data ?? []).map((o) => o.category_id)));
  const categories = await supabase
    .from("payload_categories")
    .select(PAYLOAD_CATEGORY_COLUMNS)
    .in("id", categoryIds)
    .order("label", { ascending: true })
    .returns<PayloadCategoryRow[]>();
  if (categories.error) return { ...none, error: categories.error };

  return { categories: categories.data ?? [], options: options.data ?? [], error: null };
}

const PAYLOAD_COMPOSITION_COLUMNS =
  "id,group_id,category_id,payload_option_id,cost_point,cost_weight,energy_point,energy_weight,mass_point,mass_weight,custom_point,custom_weight,trl_point,trl_weight,order_index,edited_by";

// Lists the trade-off table rows a group has built for one payload category.
export function fetchPayloadCompositions(groupId: string, categoryId: string) {
  return supabase
    .from("payload_compositions")
    .select(PAYLOAD_COMPOSITION_COLUMNS)
    .eq("group_id", groupId)
    .eq("category_id", categoryId)
    .order("order_index", { ascending: true })
    .returns<PayloadCompositionRow[]>();
}

// Adds a blank trade-off row -- the operator picks a payload and scores it afterward.
export function insertPayloadComposition(groupId: string, categoryId: string, orderIndex: number) {
  return supabase
    .from("payload_compositions")
    .insert({ group_id: groupId, category_id: categoryId, order_index: orderIndex })
    .select(PAYLOAD_COMPOSITION_COLUMNS)
    .single<PayloadCompositionRow>();
}

// Saves an edit (payload selection or a point/weight cell) to one trade-off row.
export function updatePayloadComposition(id: string, patch: Partial<PayloadCompositionRow>) {
  return supabase.from("payload_compositions").update(patch).eq("id", id);
}

// Removes a trade-off row.
export function deletePayloadComposition(id: string) {
  return supabase.from("payload_compositions").delete().eq("id", id);
}

/* ------------------------------------------------------------------ *
 * Mission risk register + matrix placement (see
 * supabase/sql/014_mission_risks.sql).
 * ------------------------------------------------------------------ */

const MISSION_RISK_COLUMNS = "id,group_id,risk_code,description,matrix_cell,order_index,edited_by";

export function fetchMissionRisks(groupId: string) {
  return supabase
    .from("mission_risks")
    .select(MISSION_RISK_COLUMNS)
    .eq("group_id", groupId)
    .order("order_index", { ascending: true })
    .returns<MissionRiskRow[]>();
}

export function insertMissionRisk(groupId: string, orderIndex: number) {
  return supabase
    .from("mission_risks")
    .insert({ group_id: groupId, order_index: orderIndex })
    .select(MISSION_RISK_COLUMNS)
    .single<MissionRiskRow>();
}

export function updateMissionRisk(id: string, patch: Partial<Pick<MissionRiskRow, "risk_code" | "description" | "matrix_cell">>) {
  return supabase.from("mission_risks").update(patch).eq("id", id);
}

export function deleteMissionRisk(id: string) {
  return supabase.from("mission_risks").delete().eq("id", id);
}

/* ------------------------------------------------------------------ *
 * Mitigation table (see supabase/sql/015_mission_mitigations.sql).
 * ------------------------------------------------------------------ */

const MISSION_MITIGATION_COLUMNS = "id,risk_id,mitigation,is_auto,order_index,edited_by";

export function fetchMissionMitigations(riskIds: string[]) {
  return supabase
    .from("mission_mitigations")
    .select(MISSION_MITIGATION_COLUMNS)
    .in("risk_id", riskIds)
    .order("order_index", { ascending: true })
    .returns<MissionMitigationRow[]>();
}

export function insertMissionMitigation(riskId: string, orderIndex: number, isAuto: boolean) {
  return supabase
    .from("mission_mitigations")
    .insert({ risk_id: riskId, order_index: orderIndex, is_auto: isAuto })
    .select(MISSION_MITIGATION_COLUMNS)
    .single<MissionMitigationRow>();
}

export function updateMissionMitigation(id: string, patch: Partial<Pick<MissionMitigationRow, "mitigation" | "risk_id" | "is_auto">>) {
  return supabase.from("mission_mitigations").update(patch).eq("id", id);
}

export function deleteMissionMitigation(id: string) {
  return supabase.from("mission_mitigations").delete().eq("id", id);
}

/* ------------------------------------------------------------------ *
 * Workshop access gating (see supabase/sql/groups.sql). All three are
 * SECURITY DEFINER RPCs -- a participant can't change their own mission
 * or group directly, and access codes are never sent to the client.
 * ------------------------------------------------------------------ */

// Saves which mission of the chosen workshop this profile is working on. Switching missions
// takes the participant out of their group server-side.
export function chooseMission(email: string, missionId: string) {
  return supabase.rpc("choose_mission", { p_email: email, p_mission_id: missionId });
}

// What the app needs to decide which screen to show: chosen mission (if
// any) and the group whose sheet the participant has unlocked (if any).
export function fetchMyWorkshopStatus(email: string) {
  return supabase.rpc("my_workshop_status", { p_email: email }).maybeSingle<WorkshopStatus>();
}

// Unlocks a group of the chosen mission -- the only way into its sheet. The
// name and the group's access code are both checked server-side. Returns
// true/false only -- never which of the two was wrong.
export function joinGroup(groupName: string, code: string) {
  return supabase.rpc("join_group", { p_group_name: groupName, p_code: code });
}

/* ------------------------------------------------------------------ *
 * Command uplink (see supabase/sql/command_uplink.sql). A group submits
 * its mission plan, which locks its sheet for good; each member then opens
 * the command uplink with the group's name and the group's uplink code.
 * ------------------------------------------------------------------ */

// The caller's group, whether its plan is submitted and whether the caller
// has opened the uplink. Null until the caller has unlocked a group.
export function fetchMyUplinkStatus() {
  return supabase.rpc("my_uplink_status").maybeSingle<UplinkStatus>();
}

// Submits the caller's group's mission plan. After this nobody in the group
// can change the sheet any more.
export function submitMissionPlan() {
  return supabase.rpc("submit_mission_plan");
}

// Opens the command uplink for the caller. Name and uplink code are both
// checked server-side; returns true/false only.
export function unlockUplink(groupName: string, code: string) {
  return supabase.rpc("unlock_uplink", { p_group_name: groupName, p_code: code });
}

// -- TLE step (see supabase/sql/tle_ground_station.sql) --

// The ground station of the caller's workshop, once the uplink is open.
export function fetchMyGroundStation() {
  return supabase.rpc("my_ground_station").maybeSingle<MyGroundStation>();
}

// The TLE the caller's group has saved, once the uplink is open.
export function fetchMyGroupTle() {
  return supabase.rpc("my_group_tle").maybeSingle<MyGroupTle>();
}

// Saves (or replaces) the group's TLE. The database checks the two lines'
// format and checksums.
export function saveGroupTle(line1: string, line2: string) {
  return supabase.rpc("save_group_tle", { p_line1: line1, p_line2: line2 });
}

/* ------------------------------------------------------------------ *
 * Launch (see supabase/sql/launch.sql). Missions offer a few launch
 * options; a group picks one on its sheet and launches it after the
 * hardware design.
 * ------------------------------------------------------------------ */

const LAUNCH_OPTION_COLUMNS =
  "id,name,vehicle,provider,summary,launch_site,launch_site_lat,launch_site_lon,orbit_altitude_km,orbit_inclination_deg,orbit_eccentricity,price_text,image_url,image_credit,video_url,order_index,tle_intl_designator,tle_catalog_base,tle_raan_deg,tle_arg_perigee_deg,tle_mean_anomaly_deg,tle_bstar,tle_mean_motion_dot,video_youtube_url,video_start_s,video_end_s";

// The launch options a mission offers, in their set order.
export async function fetchMissionLaunchOptions(missionId: string) {
  const res = await supabase
    .from("mission_launch_options")
    .select(`launch_options(${LAUNCH_OPTION_COLUMNS})`)
    .eq("mission_id", missionId)
    .returns<{ launch_options: LaunchOptionRow | null }[]>();
  const options = (res.data ?? [])
    .map((row) => row.launch_options)
    .filter((o): o is LaunchOptionRow => o !== null)
    .sort((a, b) => a.order_index - b.order_index || a.name.localeCompare(b.name));
  return { data: res.error ? null : options, error: res.error };
}

// The group's launch pick on its sheet; null until someone picks.
export function fetchGroupLaunchChoice(groupId: string) {
  return supabase
    .from("group_launch_choices")
    .select("id,group_id,launch_option_id,edited_by")
    .eq("group_id", groupId)
    .maybeSingle<GroupLaunchChoiceRow>();
}

export function upsertGroupLaunchChoice(groupId: string, launchOptionId: string | null) {
  return supabase
    .from("group_launch_choices")
    .upsert({ group_id: groupId, launch_option_id: launchOptionId }, { onConflict: "group_id" })
    .select("id,group_id,launch_option_id,edited_by")
    .single<GroupLaunchChoiceRow>();
}

// The caller's launch, once the uplink is open.
export function fetchMyLaunch() {
  return supabase.rpc("my_launch").maybeSingle<MyLaunch>();
}

// Starts the group's launch (the first press counts). Returns when it
// started, or null if the group can't launch.
export function launchGroupRocket() {
  return supabase.rpc("launch_group_rocket");
}

// The members of the caller's own group (profile id + email), used to show
// who last edited an entry on the shared sheet.
export async function fetchMyGroupMembers() {
  const res = await supabase.rpc("my_group_members");
  return { data: res.data as { profile_id: string; email: string; is_me: boolean }[] | null, error: res.error };
}

/* ------------------------------------------------------------------ *
 * Admin management (see supabase/sql/019_admin_management.sql). Every
 * write goes through a SECURITY DEFINER RPC that checks the caller's
 * profile role = 'admin' server-side, identifying the caller by their
 * Supabase Auth session (auth.uid()). The email argument every RPC still
 * takes is ignored by the database; it only remains so the signatures
 * didn't have to change.
 * ------------------------------------------------------------------ */

// -- Workshops --

// Awaited and cast directly rather than chaining .returns<T[]>() -- without
// generated Database types, postgrest-js's fluent generics treat a plain
// .rpc() result as non-array-shaped by default and refuse to cast it to an
// array type. Awaiting first and casting the resolved data sidesteps that
// entirely; the call site (`await adminListWorkshops(...)`) is unchanged.
export async function adminListWorkshops(email: string) {
  const res = await supabase.rpc("admin_list_workshops", { p_email: email });
  return { data: res.data as AdminWorkshopRow[] | null, error: res.error };
}

export function adminUpsertWorkshop(
  email: string,
  params: { id: string | null; name: string; description: string; startsAt: string | null; location: string }
) {
  return supabase
    .rpc("admin_upsert_workshop", {
      p_email: email,
      p_id: params.id,
      p_name: params.name,
      p_description: params.description,
      p_starts_at: params.startsAt,
      p_location: params.location,
    })
    .single<AdminWorkshopRow>();
}

export function adminDeleteWorkshop(email: string, id: string) {
  return supabase.rpc("admin_delete_workshop", { p_email: email, p_id: id });
}

export function adminSetActiveWorkshop(email: string, id: string) {
  return supabase.rpc("admin_set_active_workshop", { p_email: email, p_id: id });
}

export async function adminListWorkshopMissions(email: string, workshopId: string) {
  const res = await supabase.rpc("admin_list_workshop_missions", { p_email: email, p_workshop_id: workshopId });
  return { data: res.data as AdminWorkshopMissionRow[] | null, error: res.error };
}

export function adminSetWorkshopMission(email: string, workshopId: string, missionId: string) {
  return supabase.rpc("admin_set_workshop_mission", {
    p_email: email,
    p_workshop_id: workshopId,
    p_mission_id: missionId,
  });
}

export function adminRemoveWorkshopMission(email: string, workshopMissionId: string) {
  return supabase.rpc("admin_remove_workshop_mission", { p_email: email, p_workshop_mission_id: workshopMissionId });
}

// -- Groups of a workshop mission --

export async function adminListWorkshopGroups(email: string, workshopId: string) {
  const res = await supabase.rpc("admin_list_workshop_groups", { p_email: email, p_workshop_id: workshopId });
  return { data: res.data as AdminWorkshopGroupRow[] | null, error: res.error };
}

export function adminUpsertWorkshopGroup(
  email: string,
  params: { id: string | null; workshopMissionId: string; name: string; accessCode: string }
) {
  return supabase.rpc("admin_upsert_workshop_group", {
    p_email: email,
    p_id: params.id,
    p_workshop_mission_id: params.workshopMissionId,
    p_name: params.name,
    p_access_code: params.accessCode,
  });
}

// Uplink codes and plan status of a workshop's groups.
export async function adminListGroupUplinks(email: string, workshopId: string) {
  const res = await supabase.rpc("admin_list_group_uplinks", { p_email: email, p_workshop_id: workshopId });
  return { data: res.data as AdminGroupUplinkRow[] | null, error: res.error };
}

export function adminSetGroupUplinkCode(email: string, groupId: string, code: string) {
  return supabase.rpc("admin_set_group_uplink_code", { p_email: email, p_group_id: groupId, p_code: code });
}

// Undoes a group's plan submission: its sheet can be edited again and its
// members' opened uplinks are closed.
export function adminReopenMissionPlan(email: string, groupId: string) {
  return supabase.rpc("admin_reopen_mission_plan", { p_email: email, p_group_id: groupId });
}

// Ground stations of all workshops, keyed by workshop in the page.
export async function adminListGroundStations(email: string) {
  const res = await supabase.rpc("admin_list_ground_stations", { p_email: email });
  return { data: res.data as AdminGroundStationRow[] | null, error: res.error };
}

// Latitude and longitude are both given or both null (not set).
export function adminSetWorkshopGroundStation(
  email: string,
  workshopId: string,
  station: { name: string; lat: number | null; lon: number | null; altM: number }
) {
  return supabase.rpc("admin_set_workshop_ground_station", {
    p_email: email,
    p_workshop_id: workshopId,
    p_name: station.name,
    p_lat: station.lat,
    p_lon: station.lon,
    p_alt_m: station.altM,
  });
}

// Chosen launch and launch status of a workshop's groups.
export async function adminListGroupLaunches(email: string, workshopId: string) {
  const res = await supabase.rpc("admin_list_group_launches", { p_email: email, p_workshop_id: workshopId });
  return { data: res.data as AdminGroupLaunchRow[] | null, error: res.error };
}

// Every group's saved TLE in a workshop.
export async function adminListGroupTles(email: string, workshopId: string) {
  const res = await supabase.rpc("admin_list_group_tles", { p_email: email, p_workshop_id: workshopId });
  return { data: res.data as AdminGroupTleRow[] | null, error: res.error };
}

// Overwrites a group's TLE; the database checks format and checksums.
export function adminSetGroupTle(email: string, groupId: string, line1: string, line2: string) {
  return supabase.rpc("admin_set_group_tle", { p_email: email, p_group_id: groupId, p_line1: line1, p_line2: line2 });
}

// Puts a group back on the launch pad: no launch, no TLE.
export function adminResetGroupLaunch(email: string, groupId: string) {
  return supabase.rpc("admin_reset_group_launch", { p_email: email, p_group_id: groupId });
}

// Deletes the group together with its mission sheet; its members are left
// on the mission without a group.
export function adminDeleteWorkshopGroup(email: string, groupId: string) {
  return supabase.rpc("admin_delete_workshop_group", { p_email: email, p_id: groupId });
}

// -- Workshop participants --

export async function adminListWorkshopParticipants(email: string, workshopId: string) {
  const res = await supabase.rpc("admin_list_workshop_participants", { p_email: email, p_workshop_id: workshopId });
  return { data: res.data as AdminParticipantRow[] | null, error: res.error };
}

// Plans a registered person into a group (and so onto that group's mission).
// Organising only: it never unlocks the sheet -- the participant still has
// to enter the group's name and access code. Also how someone is moved to
// another group, which locks them again.
export function adminSetParticipantGroup(email: string, participantEmail: string, groupId: string) {
  return supabase.rpc("admin_set_participant_group", {
    p_email: email,
    p_participant_email: participantEmail,
    p_group_id: groupId,
  });
}

export function adminRemoveParticipant(email: string, profileId: string) {
  return supabase.rpc("admin_remove_participant", { p_email: email, p_profile_id: profileId });
}

// -- All profiles / roles --

export async function adminListProfiles(email: string) {
  const res = await supabase.rpc("admin_list_profiles", { p_email: email });
  return { data: res.data as AdminProfileRow[] | null, error: res.error };
}

export function adminSetRole(email: string, targetProfileId: string, role: "admin" | "user") {
  return supabase.rpc("admin_set_role", { p_email: email, p_target_profile_id: targetProfileId, p_role: role });
}

export function adminDeleteParticipant(email: string, targetProfileId: string) {
  return supabase.rpc("admin_delete_participant", { p_email: email, p_target_profile_id: targetProfileId });
}

// -- Missions --

const MISSION_IMAGE_MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
};

// Uploads an image into the "missions" bucket at `pathWithoutExt` + the
// file's extension and returns its public URL. Re-uploading to the same
// path overwrites the file (upsert); the ?v= suffix makes the returned URL
// differ each time, so browsers don't keep showing the replaced picture.
async function uploadMissionsBucketImage(pathWithoutExt: string, file: File) {
  const ext = MISSION_IMAGE_MIME_TO_EXT[file.type];
  if (!ext) {
    return { data: null, error: new Error("Only JPEG, JPG, or PNG images are allowed.") };
  }

  const path = `${pathWithoutExt}.${ext}`;
  const { error: uploadError } = await supabase.storage.from("missions").upload(path, file, {
    upsert: true,
    contentType: file.type,
  });
  if (uploadError) {
    return { data: null, error: uploadError };
  }

  const { data } = supabase.storage.from("missions").getPublicUrl(path);
  return { data: `${data.publicUrl}?v=${Date.now()}`, error: null };
}

// One cover image per mission, always named "cover.<ext>" in a folder keyed
// by the mission's slug, so there's never more than one per mission.
export function uploadMissionCoverImage(missionSlug: string, file: File) {
  return uploadMissionsBucketImage(`${missionSlug}/cover`, file);
}

// The picture of one payload: "payloads/<type>/<name>_<manufacturer>.<ext>".
// Payloads are shared between missions, so their pictures live in one
// "payloads" folder rather than in a mission's. The type's subfolder needs
// no setting up -- storage creates it with the first upload.
export function uploadPayloadOptionImage(path: { typeSlug: string; nameSlug: string; manufacturerSlug: string }, file: File) {
  const fileName = [path.nameSlug, path.manufacturerSlug].filter(Boolean).join("_");
  return uploadMissionsBucketImage(`payloads/${path.typeSlug}/${fileName}`, file);
}

// A payload's spec sheet PDF, for payloads whose sheet isn't linked from
// somewhere else: bucket "payloads", "<payload name>/<file name>.pdf".
// Returns the public URL, which is what goes into spec_sheet_url. Uploading
// a file with the same name again overwrites it (upsert).
export async function uploadPayloadSpecSheet(payloadSlug: string, file: File) {
  if (file.type !== "application/pdf") {
    return { data: null, error: new Error("Only PDF files are allowed.") };
  }

  // Storage keys can't hold arbitrary characters, so the file keeps its own
  // name only in a cleaned-up form.
  const baseName = file.name.replace(/\.pdf$/i, "").trim().replace(/[^A-Za-z0-9._-]+/g, "_") || "spec_sheet";
  const path = `${payloadSlug}/${baseName}.pdf`;

  const { error: uploadError } = await supabase.storage.from("payloads").upload(path, file, {
    upsert: true,
    contentType: "application/pdf",
  });
  if (uploadError) {
    return { data: null, error: uploadError };
  }

  const { data } = supabase.storage.from("payloads").getPublicUrl(path);
  return { data: data.publicUrl, error: null };
}

// -- Launch options (Launches tab) --

export async function fetchAllLaunchOptions() {
  const res = await supabase.from("launch_options").select(LAUNCH_OPTION_COLUMNS).returns<LaunchOptionRow[]>();
  const options = (res.data ?? []).sort((a, b) => a.order_index - b.order_index || a.name.localeCompare(b.name));
  return { data: res.error ? null : options, error: res.error };
}

// Which missions offer which launch options.
export async function fetchMissionLaunchAssignments() {
  const res = await supabase.from("mission_launch_options").select("mission_id,option_id");
  return { data: res.data as { mission_id: string; option_id: string }[] | null, error: res.error };
}

export function adminUpsertLaunchOption(
  email: string,
  params: Omit<LaunchOptionRow, "id" | "image_credit" | `tle_${string}` | "video_youtube_url" | "video_start_s" | "video_end_s"> & {
    id: string | null;
  }
) {
  return supabase
    .rpc("admin_upsert_launch_option", {
      p_email: email,
      p_id: params.id,
      p_name: params.name,
      p_vehicle: params.vehicle,
      p_provider: params.provider,
      p_summary: params.summary,
      p_launch_site: params.launch_site,
      p_launch_site_lat: params.launch_site_lat,
      p_launch_site_lon: params.launch_site_lon,
      p_orbit_altitude_km: params.orbit_altitude_km,
      p_orbit_inclination_deg: params.orbit_inclination_deg,
      p_orbit_eccentricity: params.orbit_eccentricity,
      p_price_text: params.price_text,
      p_image_url: params.image_url,
      p_video_url: params.video_url,
      p_order_index: params.order_index,
    })
    .single<LaunchOptionRow>();
}

export function adminSetLaunchOptionCredit(email: string, id: string, credit: string) {
  return supabase.rpc("admin_set_launch_option_credit", { p_email: email, p_id: id, p_credit: credit });
}

// What the launch page needs of one launch option beyond my_launch: the
// picture credit and the TLE details.
export function fetchLaunchOptionExtras(id: string) {
  return supabase.from("launch_options").select(LAUNCH_OPTION_COLUMNS).eq("id", id).maybeSingle<LaunchOptionRow>();
}

// Optional TLE details of a launch option; null/"" = the app picks.
export function adminSetLaunchOptionTle(
  email: string,
  id: string,
  details: Pick<
    LaunchOptionRow,
    | "tle_intl_designator"
    | "tle_catalog_base"
    | "tle_raan_deg"
    | "tle_arg_perigee_deg"
    | "tle_mean_anomaly_deg"
    | "tle_bstar"
    | "tle_mean_motion_dot"
  >
) {
  return supabase.rpc("admin_set_launch_option_tle", {
    p_email: email,
    p_id: id,
    p_intl_designator: details.tle_intl_designator,
    p_catalog_base: details.tle_catalog_base,
    p_raan_deg: details.tle_raan_deg,
    p_arg_perigee_deg: details.tle_arg_perigee_deg,
    p_mean_anomaly_deg: details.tle_mean_anomaly_deg,
    p_bstar: details.tle_bstar,
    p_mean_motion_dot: details.tle_mean_motion_dot,
  });
}

// The YouTube launch video of an option and the part of it to play.
export function adminSetLaunchOptionYoutube(email: string, id: string, video: { url: string; startS: number | null; endS: number | null }) {
  return supabase.rpc("admin_set_launch_option_youtube", {
    p_email: email,
    p_id: id,
    p_url: video.url,
    p_start_s: video.startS,
    p_end_s: video.endS,
  });
}

export function adminDeleteLaunchOption(email: string, id: string) {
  return supabase.rpc("admin_delete_launch_option", { p_email: email, p_id: id });
}

export function adminSetLaunchOptionMission(email: string, optionId: string, missionId: string, assigned: boolean) {
  return supabase.rpc("admin_set_launch_option_mission", {
    p_email: email,
    p_option_id: optionId,
    p_mission_id: missionId,
    p_assigned: assigned,
  });
}

// A launch option's picture, shown on the mission sheet: "<vehicle>.<ext>"
// in the "launches" bucket, e.g. "falcon-9.jpg". Re-uploading replaces it;
// the ?v= suffix keeps browsers from showing the old one.
export async function uploadLaunchOptionImage(vehicleSlug: string, file: File) {
  const ext = MISSION_IMAGE_MIME_TO_EXT[file.type];
  if (!ext) {
    return { data: null, error: new Error("Only JPEG, JPG, or PNG images are allowed.") };
  }
  const path = `${vehicleSlug}.${ext}`;
  const { error: uploadError } = await supabase.storage.from("launches").upload(path, file, {
    upsert: true,
    contentType: file.type,
  });
  if (uploadError) {
    return { data: null, error: uploadError };
  }
  const { data } = supabase.storage.from("launches").getPublicUrl(path);
  return { data: `${data.publicUrl}?v=${Date.now()}`, error: null };
}

// A launch option's video, played at T-0: "<option id>/video.<ext>" in the
// "launches" bucket. Re-uploading replaces it; the ?v= suffix keeps
// browsers from showing the old one.
export async function uploadLaunchVideo(optionId: string, file: File) {
  const ext = file.type === "video/webm" ? "webm" : file.type === "video/mp4" ? "mp4" : null;
  if (!ext) {
    return { data: null, error: new Error("Only MP4 or WebM videos are allowed.") };
  }
  const path = `${optionId}/video.${ext}`;
  const { error: uploadError } = await supabase.storage.from("launches").upload(path, file, {
    upsert: true,
    contentType: file.type,
  });
  if (uploadError) {
    return { data: null, error: uploadError };
  }
  const { data } = supabase.storage.from("launches").getPublicUrl(path);
  return { data: `${data.publicUrl}?v=${Date.now()}`, error: null };
}

export async function adminListAllMissions(email: string) {
  const res = await supabase.rpc("admin_list_all_missions", { p_email: email });
  return { data: res.data as AdminMissionRow[] | null, error: res.error };
}

export function adminUpsertMission(
  email: string,
  params: {
    id: string | null;
    slug: string;
    name: string;
    category: string;
    summary: string;
    objective: string;
    iconKey: string;
    platformType: MissionPlatform;
    orbitType: MissionOrbit;
    platformDetail: string | null;
    orbitDetail: string | null;
    imageUrl: string | null;
    isActive: boolean;
  }
) {
  return supabase
    .rpc("admin_upsert_mission", {
      p_email: email,
      p_id: params.id,
      p_slug: params.slug,
      p_name: params.name,
      p_category: params.category,
      p_summary: params.summary,
      p_objective: params.objective,
      p_icon_key: params.iconKey,
      p_platform_type: params.platformType,
      p_orbit_type: params.orbitType,
      p_platform_detail: params.platformDetail,
      p_orbit_detail: params.orbitDetail,
      p_image_url: params.imageUrl,
      p_is_active: params.isActive,
    })
    .single<AdminMissionRow>();
}

export function adminDeleteMission(email: string, id: string) {
  return supabase.rpc("admin_delete_mission", { p_email: email, p_id: id });
}

// -- Payload types --

export function adminUpsertPayloadCategory(
  email: string,
  params: { id: string | null; label: string; customCriterionLabel: string; orderIndex: number }
) {
  return supabase
    .rpc("admin_upsert_payload_category", {
      p_email: email,
      p_id: params.id,
      p_label: params.label,
      p_custom_criterion_label: params.customCriterionLabel,
      p_order_index: params.orderIndex,
    })
    .single<AdminPayloadCategoryRow>();
}

export function adminDeletePayloadCategory(email: string, id: string) {
  return supabase.rpc("admin_delete_payload_category", { p_email: email, p_id: id });
}

// -- Payloads --

// Offers a payload in a mission, or stops offering it (one payload, one mission per call).
export function adminSetPayloadOptionMission(email: string, optionId: string, missionId: string, assigned: boolean) {
  return supabase.rpc("admin_set_payload_option_mission", {
    p_email: email,
    p_option_id: optionId,
    p_mission_id: missionId,
    p_assigned: assigned,
  });
}

export function adminUpsertPayloadOption(
  email: string,
  params: {
    id: string | null;
    categoryId: string;
    name: string;
    manufacturer: string;
    summary: string;
    imageUrl: string | null;
    specSheetUrl: string | null;
    ratingResolution: number;
    ratingEnergy: number;
    ratingMass: number;
    ratingCost: number;
    ratingTrl: number;
    orderIndex: number;
  }
) {
  return supabase
    .rpc("admin_upsert_payload_option", {
      p_email: email,
      p_id: params.id,
      p_category_id: params.categoryId,
      p_name: params.name,
      p_manufacturer: params.manufacturer,
      p_summary: params.summary,
      p_image_url: params.imageUrl,
      p_spec_sheet_url: params.specSheetUrl,
      p_rating_resolution: params.ratingResolution,
      p_rating_energy: params.ratingEnergy,
      p_rating_mass: params.ratingMass,
      p_rating_cost: params.ratingCost,
      p_rating_trl: params.ratingTrl,
      p_order_index: params.orderIndex,
    })
    .single<AdminPayloadOptionRow>();
}

export function adminDeletePayloadOption(email: string, id: string) {
  return supabase.rpc("admin_delete_payload_option", { p_email: email, p_id: id });
}

// Checks whether an email already has a profile, for the Login tab. Only
// ever returns a boolean (via the profile_exists RPC) -- profiles has no
// SELECT policy, so this can't be used to read anyone's data.
export function checkProfileExists(email: string) {
  return supabase.rpc("profile_exists", { p_email: email });
}

// Fetches a profile's first/last name by email, for the Login tab and the
// nav bar's "logged in as" display. Only ever returns first_name/last_name
// (via the profile_lookup RPC) -- profiles has no SELECT policy, so this
// can't be used to read anyone's other data.
export async function fetchProfileByEmail(email: string) {
  const res = await supabase.rpc("profile_lookup", { p_email: email });
  return { data: res.data as { first_name: string | null; last_name: string | null; role: "admin" | "user" }[] | null, error: res.error };
}

// Ends the current Supabase Auth session during logout or account deletion cleanup.
export function signOut() {
  return supabase.auth.signOut();
}

// Saves non-anonymous profile details through the profile details RPC.
export function updateMyProfileDetails(values: {
  firstName: string;
  lastName: string;
  position: string;
  department: string;
  personalAddress: string;
}) {
  return supabase.rpc("update_my_profile_details", {
    p_first_name: values.firstName,
    p_last_name: values.lastName,
    p_position: values.position,
    p_department: values.department,
    p_personal_address: values.personalAddress,
  });
}

// Performs public database cleanup for the current account before auth-user deletion.
export function deleteMyAccountData() {
  return supabase.rpc("delete_my_account");
}

// Calls the delete-account edge function to remove the Supabase Auth user after public data cleanup.
export function deleteAuthAccount(token: string) {
  return fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-account`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: process.env.REACT_APP_SUPABASE_ANON_KEY!,
      "Content-Type": "application/json",
    },
  });
}
